"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrCadHierarchicalPortGroupingRule = void 0;
const NetNamingRules_1 = require("../../../../NetCombobulation/NetNamingRules");
const OrcadDsnPortOccurrenceUtils_1 = require("../Generators/OrcadDsnPortOccurrenceUtils");
class OrCadHierarchicalPortGroupingRule {
    constructor(reportDiagnostic = () => undefined) {
        this.reportDiagnostic = reportDiagnostic;
        this.type = NetNamingRules_1.NetNamingRuleType.Group;
    }
    apply(manager, hierarchy) {
        this.mergeHierarchicalPorts(manager, hierarchy);
        return manager;
    }
    mergeHierarchicalPorts(manager, hierarchy) {
        const emitDiagnostics = hierarchy.instance.pathUniqueId !== '__virtual_root__';
        const childrenByBoundary = new Map();
        for (const child of hierarchy.children) {
            const boundaryPath = child.instance.pathUniqueId;
            const boundaryChildren = childrenByBoundary.get(boundaryPath) ?? [];
            boundaryChildren.push(child);
            childrenByBoundary.set(boundaryPath, boundaryChildren);
        }
        for (const [boundaryPath, boundaryChildren] of childrenByBoundary) {
            const distinctPages = new Set(boundaryChildren.map((boundaryChild) => boundaryChild.instance.hierarchyEntry));
            const occurrenceNodes = new Set(boundaryChildren.map((boundaryChild) => boundaryChild.instance.instOccurence));
            const isValidatedMultiPageOccurrence = boundaryChildren.length > 1 && distinctPages.size === boundaryChildren.length && occurrenceNodes.size === 1;
            if (boundaryChildren.length !== 1 && !isValidatedMultiPageOccurrence) {
                if (emitDiagnostics) {
                    this.reportDiagnostic(`[hierarchy-port-ambiguous] boundary=${boundaryPath} hierarchyNodes=${boundaryChildren.length}`);
                }
                for (const boundaryChild of boundaryChildren) {
                    this.mergeHierarchicalPorts(manager, boundaryChild);
                }
                continue;
            }
            const child = boundaryChildren[0];
            const parentPortGroups = manager.findGroupsWithLabel('hierarchical-sheet-entry', undefined, hierarchy.instance);
            const childPortGroups = boundaryChildren.flatMap((boundaryChild) => manager.findGroupsWithLabel('hierarchical-port', undefined, boundaryChild.instance));
            const parentByIdentity = this.groupsByOccurrence(parentPortGroups, hierarchy.instance, 'hierarchical-sheet-entry', child.instance.sheetSymbol?.DbId.toString());
            const childByIdentity = new Map();
            for (const boundaryChild of boundaryChildren) {
                this.addGroupsByOccurrence(childByIdentity, manager.findGroupsWithLabel('hierarchical-port', undefined, boundaryChild.instance), boundaryChild.instance, 'hierarchical-port');
            }
            if (emitDiagnostics &&
                (parentPortGroups.length > 0 || childPortGroups.length > 0) &&
                (parentByIdentity.size === 0 || childByIdentity.size === 0)) {
                this.reportDiagnostic(`[hierarchy-port-unresolved] boundary=${boundaryPath} parentOccurrences=${parentByIdentity.size} childOccurrences=${childByIdentity.size}`);
            }
            const identityKeys = new Set([...parentByIdentity.keys(), ...childByIdentity.keys()]);
            const pairs = [];
            for (const identityKey of identityKeys) {
                const parentGroups = (parentByIdentity.get(identityKey) ?? []).filter((group) => this.hasWireOrPhysicalPin(group));
                const childGroups = (childByIdentity.get(identityKey) ?? []).filter((group) => this.hasWireOrPhysicalPin(group));
                if (parentGroups.length === 0 && childGroups.length === 0) {
                    continue;
                }
                if (parentGroups.length === 0 || childGroups.length === 0) {
                    if (emitDiagnostics) {
                        this.reportDiagnostic(`[hierarchy-port-unresolved] boundary=${boundaryPath} identity=${identityKey} parent=${parentGroups.length} child=${childGroups.length}`);
                    }
                    continue;
                }
                const childNames = this.occurrenceLabelNames(childGroups, 'hierarchical-port', identityKey);
                if (parentGroups.length !== 1 || childNames.size !== 1) {
                    if (emitDiagnostics) {
                        this.reportDiagnostic(`[hierarchy-port-ambiguous] boundary=${boundaryPath} identity=${identityKey} parent=${parentGroups.length} child=${childGroups.length}`);
                    }
                    continue;
                }
                pairs.push([parentGroups[0], childGroups]);
            }
            for (const [parentGroup, childGroups] of pairs) {
                manager.mergeGroups(parentGroup, ...childGroups.filter((childGroup) => childGroup !== parentGroup));
            }
            for (const boundaryChild of boundaryChildren) {
                this.mergeHierarchicalPorts(manager, boundaryChild);
            }
        }
    }
    occurrenceLabelNames(groups, labelType, identityKey) {
        const names = new Set();
        for (const group of groups) {
            for (const label of group.getNetLabels()) {
                const occurrence = label.strategyMetadata?.['port-occurrence'];
                if (label.labelType === labelType && occurrence && (0, OrcadDsnPortOccurrenceUtils_1.portOccurrenceIdentityKey)(occurrence) === identityKey) {
                    names.add(label.originalLabel.toUpperCase());
                }
            }
        }
        return names;
    }
    hasWireOrPhysicalPin(group) {
        for (const [, nets] of group.getNets()) {
            for (const net of nets) {
                if (net.connections.length > 0 || net.netObjects.some((object) => object.wire !== undefined)) {
                    return true;
                }
            }
        }
        return false;
    }
    groupsByOccurrence(groups, instance, labelType, sheetSymbolDbId) {
        const result = new Map();
        this.addGroupsByOccurrence(result, groups, instance, labelType, sheetSymbolDbId);
        return result;
    }
    addGroupsByOccurrence(result, groups, instance, labelType, sheetSymbolDbId) {
        for (const group of groups) {
            for (const label of group.getNetLabels(instance)) {
                if (label.labelType !== labelType) {
                    continue;
                }
                if (labelType === 'hierarchical-sheet-entry' &&
                    label.strategyMetadata?.['hierarchical-sheet-entry']?.sheetSymbolDbId !== sheetSymbolDbId) {
                    continue;
                }
                const occurrence = label.strategyMetadata?.['port-occurrence'];
                if (!occurrence) {
                    continue;
                }
                const key = (0, OrcadDsnPortOccurrenceUtils_1.portOccurrenceIdentityKey)(occurrence);
                const entries = result.get(key) ?? [];
                if (!entries.includes(group)) {
                    entries.push(group);
                    result.set(key, entries);
                }
            }
        }
    }
}
exports.OrCadHierarchicalPortGroupingRule = OrCadHierarchicalPortGroupingRule;
//# sourceMappingURL=OrcadHierarchicalPortGroupingRule.js.map